// src/features/system/tenants/hooks/useTenants.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchTenants, createTenant, deleteTenant,
  fetchTenantAttributes, writeTenantAttributes,
  type Tenant, type TenantCreateIn,
  type TenantAttributes, type TenantAttributesResponse,
} from '../api/tenantsApi';

export function useTenants() {
  const qc = useQueryClient();

  const list = useQuery<Tenant[], Error>({
    queryKey : ['tenants'],
    queryFn  : fetchTenants,
    staleTime: 60_000,
  });

  const create = useMutation({
    mutationFn: (p: TenantCreateIn) => createTenant(p),
    onSuccess : () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });

  const remove = useMutation({
    mutationFn: (idTenant: number) => deleteTenant(idTenant),
    onSuccess : () => qc.invalidateQueries({ queryKey: ['tenants'] }),
  });

  return { ...list, create, remove };
}

/**
 * Read the EAV attribute bag for a tenant. Server-side projection is
 * latest-wins per attribute, 30s TTL cached on the BFF. FE stale-time
 * 30s too so the combo is at most 60s end-to-end from write to FE
 * refresh — acceptable for non-critical metadata.
 *
 * Attributes include LLM-extracted context (business_type, notes, …)
 * plus any manual PATCH additions. Consumers render the known keys as
 * typed columns and ignore unknown keys.
 */
export function useTenantAttributes(idTenant: number | null | undefined) {
  const enabled = typeof idTenant === 'number' && idTenant > 0;
  return useQuery<TenantAttributesResponse, Error>({
    queryKey : ['tenants', idTenant, 'attributes'],
    queryFn  : () => fetchTenantAttributes(idTenant as number),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Mutation helper for PATCHing tenant EAV attributes. Invalidates the
 * matching attribute query on success so UI refreshes without a manual
 * refetch.
 */
export function useWriteTenantAttributes(idTenant: number | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attrs: TenantAttributes) =>
      writeTenantAttributes(idTenant as number, attrs, 'manual'),
    onSuccess: () => {
      if (typeof idTenant === 'number') {
        qc.invalidateQueries({ queryKey: ['tenants', idTenant, 'attributes'] });
      }
    },
  });
}